<script lang="ts">
	import type { MultipleLineChartOption } from "@hitit/new_ui/src";
	import type { Resolution } from "@hitit/hub/src/v7/utils";
	//
	import { ChartIcon, MultipleLineChart } from "@hitit/new_ui/src";

	import { api, t } from "@hitit/hub/src/stores";
	import { formatISO } from "date-fns";
	import { onDestroy } from "svelte";
	import {
		getAllDateInRange,
		getChartDateKey,
		getResolutionOfRange,
		xAxisLabel,
		chartToolTipDateFormat,
		formatKrwNumber,
	} from "@hitit/hub/src/v7/utils";
	export let storeIds: string[] = [];
	export let range = {
		start: new Date(),
		end: new Date(),
	};
	type ChartData = {
		value: number;
		date: Date;
		class: string;
	};
	const areaGradient = [
		{
			dataClass: "discount",
			stopDatas: [
				{
					offset: "0%",
					style: "stop-color:#e67570;stop-opacity:0.1",
				},
				{
					offset: "90%",
					style: "stop-color:white;stop-opacity:0.1",
				},
			],
		},
		{
			dataClass: "redeemed",
			stopDatas: [
				{
					offset: "0%",
					style: "stop-color:#bcbfc3;stop-opacity:0.1",
				},
				{
					offset: "90%",
					style: "stop-color:white;stop-opacity:0.1",
				},
			],
		},
	];

	// Datas
	let svg: SVGSVGElement;
	let tooltip: HTMLDivElement;
	let chart: HTMLDivElement;
	let parseDate = "";
	let discount = "";
	let redeemed = "";
	//For Chart
	let datas: ChartData[] = [];
	let allDates: Date[] = [];
	let __resizeEvent__ = () => {};
	let resolution: Resolution = "HOURLY";

	function getReport(
		range: { start: Date; end: Date },
		storeId: string,
		resolution: string
	) {
		const { start, end } = range;
		return $api.getStoreSalesReport({
			startTimeInTimeZone: formatISO(start),
			endTimeInTimeZone: formatISO(end),
			reportTimeZone: "ASIA_SEOUL",
			reportResolution: resolution,
			storeid: storeId,
		});
	}

	const mousemoveEvent = (event: MouseEvent, datas: ChartData[]) => {
		const { clientX, clientY } = event;

		const [redeemdData, discountData] = datas;
		parseDate = chartToolTipDateFormat(discountData.date, resolution);
		discount = formatKrwNumber(discountData.value);
		redeemed = formatKrwNumber(redeemdData.value);

		tooltip.style.display = "flex";

		const { innerWidth } = window;
		const { width } = tooltip.getBoundingClientRect();
		tooltip.style.top = clientY + 20 + "px";
		if (innerWidth > clientX + 20 + width) {
			tooltip.style.left = clientX + 20 + "px";
		} else {
			let left = clientX - (20 + width);
			if (left < 0) {
				left = left * -1;
			}

			tooltip.style.left = left + "px";
		}
	};
	const mouseoutEvent = (e: MouseEvent) => {
		tooltip.style.display = "none";
	};

	const sorting = (a: string, b: string) => {
		const index = ["redeemed", "discount"];
		const parseA = index.findIndex((v) => v === a);
		const parseB = index.findIndex((v) => v === b);

		return parseA - parseB;
	};

	//Do
	$: (async (__storeIds: string[], __range: { start: Date; end: Date }) => {
		try {
			resolution = getResolutionOfRange(range);
			allDates = getAllDateInRange(range, resolution);
			const map = new Map();
			const originStoreIds = storeIds;
			const originRange = range;

			const allRevenue = __storeIds.map((id) =>
				getReport(__range, id, resolution)
			);
			const promises = await Promise.all(allRevenue);
			//
			if (originStoreIds !== __storeIds || originRange !== __range) {
				console.log("Canceld");
				return;
			}

			for (const result of promises) {
				const salesReports = result.data.salesReports;
				for (const salesData of salesReports) {
					const { year, month, day, hour, discount, redeemed } = salesData;
					const d_key = `Discount-${year}-${month}-${day}-${hour}`;
					const r_key = `Redeemed-${year}-${month}-${day}-${hour}`;
					const originDValue = map.get(d_key) || 0;
					const originRValue = map.get(r_key) || 0;
					map.set(d_key, originDValue + discount);
					map.set(r_key, originRValue + redeemed * -1);
				}
			}

			const D = allDates.map((v) => {
				return {
					date: v,
					value: map.get("Discount-" + getChartDateKey(v, resolution)) || 0,
					class: "discount",
				};
			});

			const R = allDates.map((v) => {
				return {
					date: v,
					value: map.get("Redeemed-" + getChartDateKey(v, resolution)) || 0,
					class: "redeemed",
				};
			});
			datas = [...D, ...R];
		} catch (error) {
			console.log(error);
		}
	})(storeIds, range);

	$: if (chart && datas) {
		if (svg) {
			svg.remove();
			window.removeEventListener("resize", __resizeEvent__);
		}
		const chartOption: MultipleLineChartOption<ChartData> = {
			classification: "class",
			y: (d: ChartData) => d.value,
			margin: {
				left: 15,
				right: 15,
				top: 15,
				bottom: 40,
			},
			areaGradient,
			xAxislabel: xAxisLabel(resolution, allDates),
			yDomainCorrection: (yMax) => (yMax > 50 ? yMax : 50),
			sorting,
			mousemoveEvent,
			mouseoutEvent,
		};
		const { node, resizeEvent } = MultipleLineChart(chart, datas, chartOption);
		svg = node as SVGSVGElement;
		__resizeEvent__ = resizeEvent;
	}
	onDestroy(() => {
		window.removeEventListener("resize", __resizeEvent__);
	});

</script>

<div class="container">
	<div class="header">
		<span class="icon">
			<ChartIcon fill={'#f66d6c'} />
		</span>
		<p>{$t('hub.dashboard.graph.Discount_Redeemed_Trend')}</p>
	</div>
	<div class="chart" bind:this={chart} />
</div>
<div id="tooltip" bind:this={tooltip}>
	<div class="date">{parseDate}</div>
	<div class="discount box">
		<div class="color-icon" />
		{$t('global.discount') + ' : '}
		{discount}{$t('global.KRW')}
	</div>
	<div class="redeemed box">
		<div class="color-icon" />
		{$t('global.point.usedPoint') + ' : '}
		{redeemed}{$t('global.KRW')}
	</div>
</div>

<style lang="scss">
	@import "../../../variables.scss";
	#tooltip {
		position: fixed;
		z-index: 9;
		display: none;
		min-width: 200px;
		max-width: calc(100vw - 80px);
		flex-direction: column;
		border-radius: 5px;
		background-color: rgba($color: #46474b, $alpha: 0.7);
		padding: 20px;
		gap: 10px;

		.date {
			color: white;
			font-weight: 550;
			font-size: 1.25rem;
			text-align: center;
		}
		.box {
			display: flex;
			align-items: center;
			font-size: 1.25rem;
			gap: 10px;
			width: 100%;
			color: white;
		}
		.discount .color-icon {
			background-color: #f66d6c;
		}
		.redeemed .color-icon {
			background-color: #bcbfc3;
		}

		.color-icon {
			width: 16px;
			height: 16px;
		}
	}

	.container {
		display: flex;
		flex-direction: column;
		gap: 10px;
		box-sizing: border-box;
		width: 100%;
		height: 100%;
		padding: 20px 0;

		.chart {
			height: 421px;
			:global(svg .axisGroup text) {
				font-size: 0.875rem;
				fill: #6d6f76;
				font-weight: 200;
			}
			:global(svg path#discount) {
				stroke: #e67570;
				animation: dash 1s linear forwards;
			}

			:global(svg path#redeemed) {
				stroke: #bcbfc3;
				animation: dash 1s linear forwards;
			}
			:global(svg circle[dataType="redeemed"]) {
				transition: all 0.2s;
				fill: white;
				stroke: #bcbfc3;
			}

			:global(svg circle[dataType="discount"]) {
				transition: all 0.2s;
				fill: white;
				stroke: #f36464;
			}
			:global(svg circle[dataType="discount"].show) {
				stroke: #f66d6c;
				fill: #f66d6c;
				opacity: 1 !important;
			}
			:global(svg circle[dataType="discount"].hide) {
				opacity: 0;
				fill: #f66d6c;
			}
			:global(svg line[dataType="discount"]) {
				stroke: #f66d6c;
			}
			/*  */
			:global(svg circle[dataType="redeemed"]) {
				transition: all 0.2s;
				fill: white;
				stroke: #bcbfc3;
			}
			:global(svg circle[dataType="redeemed"].show) {
				stroke: #bcbfc3;
				fill: #bcbfc3;
				opacity: 1 !important;
			}
			:global(svg circle[dataType="redeemed"].hide) {
				opacity: 0;
				fill: #bcbfc3;
			}
			:global(svg line[dataType="redeemed"]) {
				stroke: #bcbfc3;
			}

			:global(svg path#area-discount),
			:global(svg path#area-redeemed) {
				opacity: 0;
				animation-delay: 1s;
				animation-duration: 1s;
				animation-name: showup;
				animation-fill-mode: forwards;
			}
		}

		.header {
			display: flex;
			align-items: center;
			width: 100%;
			gap: 10px;
			padding: 0px 10px;
			box-sizing: border-box;
			.icon {
				width: 24px;
				height: 24px;
			}
			p {
				font-weight: 550;
				font-size: 1.25rem;
				color: #46474b;
			}
		}
	}
	@include DesktopSize {
		.container {
			.header {
				padding: 0px 30px;
			}
			.chart {
				height: 527px;
			}
		}
	}

	@keyframes dash {
		to {
			stroke-dashoffset: 0;
		}
	}
	@keyframes showup {
		to {
			opacity: 1;
		}
	}

</style>
